<template>
  <NoCustomerState v-if="!selectedCustomerId" :hasCustomers="hasCustomers" />
  <div v-else class="tw-flex tw-flex-col tw-gap-8">
    <!-- filter header with export button -->
    <div class="tw-flex tw-justify-end tw-items-center tw-w-full tw-gap-4">
      <StatisticsFilter @update:filters="handleFiltersChange" />
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <button
            class="tw-bg-secondary-500 tw-text-sm tw-text-white tw-px-4 tw-py-2 tw-rounded tw-cursor-pointer tw-flex tw-items-center tw-gap-2"
            :class="{
              'tw-opacity-50': isExporting,
              'tw-cursor-progress': isExporting,
            }"
            type="button"
            :disabled="isExporting"
          >
            {{ t('common.actions.export') }}
            <ChevronDown class="tw-w-4 tw-h-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem @click="handleExportStatistics('pdf')">PDF</DropdownMenuItem>
          <DropdownMenuItem @click="handleExportStatistics('xlsx')">XLSX</DropdownMenuItem>
          <DropdownMenuItem @click="handleExportStatistics('csv')">CSV</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
    <!-- end filter header -->
    <!-- Statistics Content Container -->
    <div id="statistics-content" class="tw-flex tw-flex-col tw-gap-8">
      <!--    overview -->
      <StatisticsOverview
        :startDate="filters.startDate"
        :endDate="filters.endDate"
        :customerId="filters.customerId"
        :includeRecurring="filters.includeRecurring"
      />
      <!--    end overview -->
      <!-- sections -->
      <NumberOfRequests
        :startDate="filters.startDate"
        :endDate="filters.endDate"
        :customerId="filters.customerId"
        :includeRecurring="filters.includeRecurring"
      />
      <OrderVolume
        :startDate="filters.startDate"
        :endDate="filters.endDate"
        :customerId="filters.customerId"
        :includeRecurring="filters.includeRecurring"
      />
      <PaymentVolume
        :startDate="filters.startDate"
        :endDate="filters.endDate"
        :customerId="filters.customerId"
        :includeRecurring="filters.includeRecurring"
      />
      <OrderCommitmentTrend
        :startDate="filters.startDate"
        :endDate="filters.endDate"
        :customerId="filters.customerId"
        :includeRecurring="filters.includeRecurring"
      />
      <OrderBalanceTrend
        :startDate="filters.startDate"
        :endDate="filters.endDate"
        :customerId="filters.customerId"
        :includeRecurring="filters.includeRecurring"
      />
    </div>
  </div>
</template>

<script setup>
import StatisticsFilter from '@/views/Statistics/sections/StatisticsFilter.vue';
import StatisticsOverview from '@/views/Statistics/sections/StatisticsOverview.vue';
import NumberOfRequests from '@/views/Statistics/sections/NumberOfRequests.vue';
import OrderVolume from '@/views/Statistics/sections/OrderVolume.vue';
import PaymentVolume from '@/views/Statistics/sections/PaymentVolume.vue';
import OrderCommitmentTrend from '@/views/Statistics/sections/OrderCommitmentTrend.vue';
import OrderBalanceTrend from '@/views/Statistics/sections/OrderBalanceTrend.vue';
import NoCustomerState from '@/components/shared/NoCustomerState.vue';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { ref, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useDashboardStore } from '@/stores/dashboard.store';
import { useCustomerStore } from '@/stores/customer.store';
import { ChevronDown } from 'lucide-vue-next';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';

const { exportStatistics } = useDashboardStore();
const { hasCustomers, selectedCustomerId } = storeToRefs(useCustomerStore());

const { t } = useI18n();
const { setBreadcrumbs } = useBreadcrumbs();

const isExporting = ref(false);
const filters = ref({
  startDate: '',
  endDate: '',
  customerId: null,
  includeRecurring: true,
});

const handleFiltersChange = values => {
  filters.value.customerId = values.customerId;
  filters.value.startDate = values.startDate;
  filters.value.endDate = values.endDate;
  filters.value.includeRecurring = values.includeRecurring;
};

const handleExportStatistics = async format => {
  if (isExporting.value) return;

  isExporting.value = true;
  const response = await exportStatistics(
    filters.value.startDate,
    filters.value.endDate,
    filters.value.customerId,
    format,
    filters.value.includeRecurring
  );
  isExporting.value = false;
  window.open(response.file_url, '_blank');
};

onMounted(() => {
  setBreadcrumbs([
    {
      labelKey: 'sidebar.items.statistics',
    },
  ]);
});
</script>
